import React, { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import { X } from 'react-feather';

export interface FormDisclaimerModalProps {
  open: boolean;
  onClose: () => void;
  onSyncToIConnect: () => void;
  onSubmit: () => void;
  title?: string;
  disclaimerText?: string;
}

const DEFAULT_TITLE = 'Disclaimer';
const DEFAULT_TEXT = 'In case of syncing to iConnect, please note that the draft has been pushed to iConnect. Please use iConnect to submit the final record.';

export const FormDisclaimerModal: React.FC<FormDisclaimerModalProps> = ({
  open,
  onClose,
  onSyncToIConnect,
  onSubmit,
  title = DEFAULT_TITLE,
  disclaimerText = DEFAULT_TEXT,
}) => {
  const [showSynced, setShowSynced] = useState(false);

  const onModalClose = useCallback(() => {
    setShowSynced(false);
    onClose();
  }, [onClose]);

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onModalClose} open={open}>
      <Header>
        <Title>{title}</Title>
        <StyledX onClick={onModalClose} />
      </Header>
      <Content>
        <b>Please Note:</b> {disclaimerText}
        {showSynced && <SyncedMessage>Synced to iConnect and form submitted.</SyncedMessage>}
      </Content>
      <ButtonsContainer>
        <Button
          onClick={async () => {
            setShowSynced(false);
            try {
              await onSyncToIConnect();
              setShowSynced(true);
            } catch {
              setShowSynced(false);
            }
          }}
          variant="contained"
          sx={{
            textTransform: 'none',
            backgroundColor: 'rgb(255, 161, 146)',
            color: '#000',
            '&:hover': {
              backgroundColor: 'rgb(255, 140, 126)',
            },
          }}
        >
          Sync to iConnect
          <br />
          (For J&J Employees)
        </Button>
        <Button
          onClick={onSubmit}
          variant="outlined"
          sx={{
            textTransform: 'none',
            color: '#000',
            borderColor: '#000',
          }}
        >
          Submit to AIDA Only
          <br />
          (Contract Team Only)
        </Button>
      </ButtonsContainer>
    </Dialog>
  );
};

const StyledX = styled(X)({
  cursor: 'pointer',
  position: 'absolute',
  top: 15,
  right: 10,
  height: 24,
  width: 24,
});

const Header = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '15px 10px',
  borderBottom: '1px solid #d0d0d0',
  position: 'relative',
});

const Title = styled.div({
  fontSize: 20,
  fontWeight: 700,
  color: '#000',
});

const Content = styled.div({
  padding: 30,
  whiteSpace: 'pre-line',
});

const ButtonsContainer = styled.div({
  display: 'flex',
  justifyContent: 'space-around',
  gap: 16,
  padding: 30,
});

const SyncedMessage = styled.div({
  padding: '0',
  color: '#2E7D32',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  marginTop: 10,
});

export default FormDisclaimerModal;
